import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Mail, MapPin, Send, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Logo from './Logo';
import { subscribeNewsletter } from '../lib/api';

function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | loading | done

  const submit = async (e) => {
    e.preventDefault();
    const val = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setState('loading');
    try {
      const res = await subscribeNewsletter(val);
      setState('done');
      toast.success(res.already ? "You're already subscribed — thank you!" : 'Subscribed! Watch your inbox for Chamber news.');
    } catch {
      setState('idle');
      toast.error('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="mt-8" data-testid="newsletter-signup">
      <h4 className="font-sans-ah tracking-nav text-[12px] text-[#8fc0e2] mb-3">STAY IN THE LOOP</h4>
      {state === 'done' ? (
        <div className="flex items-center gap-2 font-sans-ah text-[14px] text-white/80" data-testid="newsletter-success">
          <span className="w-7 h-7 rounded-full bg-[#4a9cd4] flex items-center justify-center shrink-0"><Check size={15} /></span>
          You're on the list!
        </div>
      ) : (
        <form onSubmit={submit} className="flex items-stretch gap-2 max-w-[340px]">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            aria-label="Email address for newsletter"
            data-testid="newsletter-email-input"
            className="flex-1 min-w-0 px-3.5 py-2.5 rounded-sm bg-white/10 border border-white/20 text-white placeholder:text-white/40 font-sans-ah text-[14px] focus:outline-none focus:border-[#4a9cd4] transition-colors"
          />
          <button
            type="submit"
            disabled={state === 'loading'}
            data-testid="newsletter-submit-btn"
            aria-label="Subscribe to newsletter"
            className="shrink-0 w-11 flex items-center justify-center rounded-sm bg-[#4a9cd4] hover:bg-[#3d7ca6] text-white transition-colors disabled:opacity-60"
          >
            {state === 'loading' ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
          </button>
        </form>
      )}
      <p className="font-sans-ah text-[12px] text-white/45 font-300 mt-2.5">Chamber news &amp; events. No spam — unsubscribe anytime.</p>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="bg-[#1f2c33] text-white pt-16 pb-8">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-4 gap-10 pb-12 border-b border-white/10">
          <div className="md:col-span-1">
            <Logo variant="white" />
            <p className="font-sans-ah text-[13px] text-white/60 font-300 leading-relaxed mt-5">
              The voice for a prosperous business community across the Almaguin Highlands.
            </p>
            <div className="flex gap-3 mt-5">
              <a href="https://www.facebook.com/almaguinhighlandschamber" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook-link" className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#4a9cd4] hover:border-[#4a9cd4] transition-colors">
                <Facebook size={16} />
              </a>
            </div>
            <NewsletterSignup />
          </div>

          <div>
            <h4 className="font-sans-ah tracking-nav text-[12px] text-[#8fc0e2] mb-5">EXPLORE</h4>
            <ul className="space-y-3">
              {[['About the Chamber', '/about'], ['Membership', '/membership'], ['Business Directory', '/directory'], ['News & Events', '/news'], ['Contact', '/contact']].map(([l, h]) => (
                <li key={l}>
                  <Link to={h} className="font-sans-ah text-[14px] text-white/70 font-300 hover:text-white transition-colors">{l}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-sans-ah tracking-nav text-[12px] text-[#8fc0e2] mb-5">MEMBERSHIP</h4>
            <ul className="space-y-3">
              {[['Benefits', '/benefits'], ['Join the Chamber', '/membership'], ['Member Directory', '/directory'], ['Admin Login', '/admin']].map(([l, h]) => (
                <li key={l}>
                  <Link to={h} className="font-sans-ah text-[14px] text-white/70 font-300 hover:text-white transition-colors">{l}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-sans-ah tracking-nav text-[12px] text-[#8fc0e2] mb-5">GET IN TOUCH</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 font-sans-ah text-[14px] text-white/70 font-300">
                <MapPin size={17} className="text-[#4a9cd4] shrink-0 mt-0.5" /> Almaguin Highlands, Ontario, Canada
              </li>
              <li className="flex items-center gap-3 font-sans-ah text-[14px] text-white/70 font-300">
                <Mail size={17} className="text-[#4a9cd4] shrink-0" /> almaguinhighlandschamber@gmail.com
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8">
          <p className="font-sans-ah text-[12px] text-white/50 tracking-wide-ah">
            &copy; {new Date().getFullYear()} Almaguin Highlands Chamber of Commerce. All rights reserved.
          </p>
          <a
            href="https://explorealmaguin.ca/"
            target="_blank"
            rel="noreferrer"
            className="group flex items-center gap-3"
            aria-label="Invest In Almaguin - explorealmaguin.ca"
          >
            <span className="bg-white rounded-md px-4 py-2.5 flex items-center transition-transform duration-300 group-hover:-translate-y-0.5">
              <img src="/assets/invest-in-almaguin.png" alt="Invest In Almaguin" className="h-11 w-auto" />
            </span>
            <span className="font-sans-ah text-[12px] tracking-wide-ah text-[#8fc0e2] group-hover:text-white transition-colors">
              explorealmaguin.ca
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
}
